/**
 * Loading and switching the UI locale at runtime (L1). The build serves each locale's copy in parts
 * (vite/i18n-plugin.ts): the first catalogue, the rest, and one module per route group's namespace (`app.cases`);
 * this runtime loads only the active locale's parts, each when it is first needed, and adds them to the i18n
 * instance. Switching the locale loads the new locale's parts that are in use, then changes the language: no
 * reload, no raw keys, no suspense flash.
 *
 * The loaders come from `virtual:brevalt-i18n/locales` (locale-runtime.ts); tests pass their own.
 */
import { use } from "react";
import { useTranslation } from "react-i18next";
import type { i18n as I18n } from "i18next";
import { setActiveLocale, storeLocale } from "./locale";

type Copy = Record<string, string>;
type Load = () => Promise<{ default: Copy }>;

export interface LocaleLoaders {
  SOURCE_LOCALE: string;
  LOCALES: readonly string[];
  initial: Record<string, Load>;
  rest: Record<string, Load>;
  namespaces: Record<string, Record<string, Load>>;
}

/** Part ids: `""` the first catalogue, `"rest"`, or a namespace. */
const INITIAL = "";
const REST = "rest";

export function createLocaleRuntime(loaders: LocaleLoaders) {
  const promises = new Map<string, Promise<Copy>>();
  const loaded = new Map<string, Copy>();
  /** The parts this page uses (any locale): a switch loads them for the new locale before it changes language. */
  const used = new Set<string>();
  const added = new WeakMap<I18n, Set<string>>();
  const id = (lang: string, part: string) => `${lang}\0${part}`;

  function loader(lang: string, part: string): Load {
    const table = part === INITIAL ? loaders.initial : part === REST ? loaders.rest : loaders.namespaces[part];
    const load = table?.[lang] ?? table?.[loaders.SOURCE_LOCALE];
    if (!load) throw new Error(`i18n: no copy "${part || "initial"}" for ${lang}`);
    return load;
  }

  function load(lang: string, part: string): Promise<Copy> {
    const key = id(lang, part);
    let p = promises.get(key);
    if (!p) {
      const pending: Promise<Copy> & { status?: string; value?: Copy } = loader(lang, part)().then(
        (m) => {
          loaded.set(key, m.default);
          // React's thenable protocol: `use()` reads a fulfilled promise at once, without suspending again.
          pending.status = "fulfilled";
          pending.value = m.default;
          return m.default;
        },
        (e: unknown) => {
          // A failed load (offline, a new deployment) may be tried again by the next render, not stuck rejected.
          promises.delete(key);
          throw e;
        },
      );
      p = pending;
      promises.set(key, p);
    }
    return p;
  }

  function add(i18n: I18n, lang: string, part: string, copy: Copy): void {
    let done = added.get(i18n);
    if (!done) added.set(i18n, (done = new Set()));
    const key = id(lang, part);
    if (done.has(key)) return;
    // Deep, and without overwrite: what is in the instance already wins (the pending keys of en, a test's copy).
    i18n.addResourceBundle(lang, "translation", copy, true, false);
    done.add(key);
  }

  const languageOf = (i18n: I18n) => i18n.resolvedLanguage ?? i18n.language;

  return {
    locales: loaders.LOCALES,
    source: loaders.SOURCE_LOCALE,
    load,
    /** The part when it is loaded already, else null. */
    peek: (lang: string, part: string): Copy | null => loaded.get(id(lang, part)) ?? null,

    /** Starts loading parts of the active (or given) locale ahead of their pages (a route's import). */
    preload(lang: string, ...parts: string[]): Promise<unknown> {
      return Promise.all(parts.map((p) => load(lang, p)));
    },

    /**
     * Adds a part of the instance's language to it, suspending until it is loaded (a `<Suspense>` above shows
     * the pending page meanwhile). Safe on every render.
     */
    usePart(i18n: I18n, part: string): void {
      used.add(part);
      const lang = languageOf(i18n);
      // Always through use(): React wants the same calls on the retry after a suspension. A loaded part's promise is
      // marked fulfilled, so use() returns it without suspending.
      add(i18n, lang, part, use(load(lang, part)));
    },

    /** Adds a part of `lang` (loaded) to the instance. */
    add,

    /**
     * Switches the instance to `lang`: loads its first catalogue and every part in use, adds them, then changes the
     * language (`<html lang>`, the formatting locale and this browser's stored choice follow).
     */
    async switchTo(i18n: I18n, lang: string, { remember = true }: { remember?: boolean } = {}): Promise<void> {
      if (!loaders.LOCALES.includes(lang)) throw new Error(`i18n: ${lang} is not one of this app's locales`);
      const parts = [INITIAL, ...used];
      const copies = await Promise.all(parts.map((p) => load(lang, p)));
      parts.forEach((p, i) => add(i18n, lang, p, copies[i] ?? {}));
      if (languageOf(i18n) !== lang) await i18n.changeLanguage(lang);
      setActiveLocale(lang);
      if (remember) storeLocale(lang);
    },
  };
}

export type LocaleRuntime = ReturnType<typeof createLocaleRuntime>;

/** A route group's namespace (`app.cases`) in the active locale; suspends the first time it loads. */
export function useNamespaceCopy(runtime: LocaleRuntime, namespace: string): void {
  const { i18n } = useTranslation();
  runtime.usePart(i18n, namespace);
}
